import { formatCount, formatTimestamp, formatToman, statusLabel } from '@saay/formatting';
import {
  AccordionPanel,
  AssistChip,
  Banner,
  CapacityMeter,
  ChipRow,
  DetailGrid,
  FactList,
  SkeletonShape,
  Table,
} from '../kit.js';
import type { ReactNode } from 'react';

import type { TargetingSnapshot } from '../api.js';
import { targetText } from '../display.js';

export { targetText } from '../display.js';
import {
  useActiveOrganizationRoles,
  useActiveOrganizations,
  useCities,
  useProvinces,
} from '../queries.js';

/**
 * A targeting impact report (AC §18.9.1 `TargetingSnapshot`): the preview, a run's approved snapshot,
 * and the `CREDIT_PROGRAM_TARGETING_RUN` approval request all show the server's figures this way.
 */

/** One-line impact headline (ADM-58). */
export function targetingImpactHeadline(snapshot: TargetingSnapshot): string {
  return `${formatCount(snapshot.recipientCount)} نفر × ${formatToman(snapshot.amountPerRecipientIrr)} = ${formatToman(snapshot.totalCommitmentIrr)}`;
}

function exclusionTotal(snapshot: TargetingSnapshot): number {
  return snapshot.exclusions.reduce((sum, row) => sum + row.count, 0);
}

/**
 * The commitment meter of an impact report (ADM-58): the program exposure after the run, the
 * excluded count, and the cap refusal. The one-line headline is shown by the report itself.
 */
export function TargetingImpactMeter({ snapshot }: { readonly snapshot: TargetingSnapshot }) {
  const capRate =
    snapshot.programExposure.capIrr <= 0
      ? '0'
      : String(Math.min(1, snapshot.programExposure.afterRunIrr / snapshot.programExposure.capIrr));
  const excluded = exclusionTotal(snapshot);
  return (
    <>
      <CapacityMeter
        label="تعهد برنامه پس از اجرا"
        rate={capRate}
        valueText={`${formatToman(snapshot.programExposure.afterRunIrr)} از ${formatToman(snapshot.programExposure.capIrr)}`}
      />
      {excluded === 0 ? null : (
        <ChipRow>
          <AssistChip>{`${formatCount(excluded)} کنارگذاشته`}</AssistChip>
        </ChipRow>
      )}
      {snapshot.capExceeded ? (
        <Banner tone="danger">
          این اجرا از سقف کل تعهد برنامه عبور می‌کند و در زمان اجرا رد می‌شود؛ دامنه را محدود کنید.
        </Banner>
      ) : null}
    </>
  );
}

/** The stored impact report of a run or a preview (AC §18.9.1), exactly as the server returned it. */
export function SnapshotFacts({ snapshot }: { readonly snapshot: TargetingSnapshot }) {
  const organizations = useActiveOrganizations(snapshot.breakdown.length > 0);
  const roles = useActiveOrganizationRoles(snapshot.breakdown.length > 0);
  const cities = useCities(
    snapshot.target.type === 'GEOGRAPHY' ? snapshot.target.provinceId : undefined,
  );
  const provinces = useProvinces();
  const lookupName = (
    id: string,
    resolve: (id: string) => string | undefined,
    pending: boolean,
  ): ReactNode => {
    const name = resolve(id);
    if (name !== undefined) {
      return name;
    }
    return pending ? <SkeletonShape label="در حال بارگذاری نام…" lines={1} variant="lines" /> : '—';
  };
  const organizationName = (id: string): ReactNode =>
    lookupName(
      id,
      (value) => organizations.data?.find((item) => item.id === value)?.displayNameFa,
      organizations.isPending,
    );
  const roleName = (id: string): ReactNode =>
    lookupName(
      id,
      (value) => roles.data?.find((item) => item.id === value)?.nameFa,
      roles.isPending,
    );
  const cityName = (id: string): ReactNode =>
    lookupName(
      id,
      (value) => cities.data?.find((item) => item.id === value)?.nameFa,
      cities.isPending,
    );
  const namesPending =
    organizations.isPending || roles.isPending || provinces.isPending || cities.isPending;
  const audience = namesPending ? (
    <SkeletonShape label="در حال بارگذاری مخاطب…" lines={1} variant="lines" />
  ) : (
    targetText(snapshot.target, {
      cityName: (id) => cities.data?.find((item) => item.id === id)?.nameFa,
      organizationName: (id) => organizations.data?.find((item) => item.id === id)?.displayNameFa,
      provinceName: (id) => provinces.data?.find((item) => item.id === id)?.nameFa,
      roleName: (id) => roles.data?.find((item) => item.id === id)?.nameFa,
    })
  );
  const excluded = exclusionTotal(snapshot);
  const detail = (
    <>
      <DetailGrid
        items={[
          {
            key: 'recipients',
            label: 'دریافت‌کننده',
            value: `${formatCount(snapshot.recipientCount)} نفر`,
          },
          {
            key: 'amount',
            label: 'مبلغ هر نفر',
            value: formatToman(snapshot.amountPerRecipientIrr),
          },
          {
            key: 'total',
            label: 'اعتبار موردنیاز',
            value: formatToman(snapshot.totalCommitmentIrr),
          },
          {
            key: 'after',
            label: 'تعهد پس از اجرا',
            value: `${formatToman(snapshot.programExposure.afterRunIrr)} از ${formatToman(snapshot.programExposure.capIrr)}`,
          },
        ]}
      />
      <FactList
        items={[
          { key: 'target', label: 'هدف', value: audience },
          {
            key: 'validity',
            label: 'اعتبار',
            value: `از ${formatTimestamp(snapshot.validity.validFrom, { withTime: false })}${
              snapshot.validity.validUntil === null
                ? ' بدون انقضا'
                : ` تا ${formatTimestamp(snapshot.validity.validUntil, { withTime: false })}`
            }`,
          },
          {
            key: 'committed',
            label: 'تعهد فعلی برنامه',
            value: formatToman(snapshot.programExposure.committedIrr),
          },
          {
            key: 'exclusions',
            label: 'کنارگذاشته',
            value:
              snapshot.exclusions.length === 0 ? (
                'ندارد'
              ) : (
                <ul className="admin-plain-list">
                  {snapshot.exclusions.map((row) => (
                    <li key={row.reasonCode}>
                      {`${statusLabel('targetingExclusionCode', row.reasonCode)}: ${formatCount(row.count)} نفر`}
                    </li>
                  ))}
                </ul>
              ),
          },
        ]}
      />
      {snapshot.breakdown.length === 0 ? null : (
        <Table
          caption="تفکیک بر اساس سازمان و نقش"
          columns={[
            {
              header: 'سازمان',
              key: 'org',
              pin: 'start',
              render: (row) =>
                row.organizationDisplayNameFa ?? organizationName(row.organizationId),
            },
            {
              header: 'نقش',
              key: 'role',
              render: (row) =>
                row.organizationRoleId === null ? 'همه نقش‌ها' : roleName(row.organizationRoleId),
            },
            {
              header: 'نفر',
              key: 'count',
              numeric: true,
              render: (row) => formatCount(row.recipientCount),
            },
            {
              header: 'مبلغ',
              key: 'total',
              numeric: true,
              pin: 'end',
              render: (row) => formatToman(row.totalIrr),
            },
          ]}
          rowKey={(row) => `${row.organizationId}:${row.organizationRoleId ?? ''}`}
          rows={snapshot.breakdown}
        />
      )}
      {snapshot.geographyBreakdown.length === 0 ? null : (
        <Table
          caption="تفکیک جغرافیایی"
          columns={[
            { header: 'شهر', key: 'city', pin: 'start', render: (row) => cityName(row.cityId) },
            {
              header: 'نفر',
              key: 'count',
              numeric: true,
              render: (row) => formatCount(row.recipientCount),
            },
            {
              header: 'مبلغ',
              key: 'total',
              numeric: true,
              pin: 'end',
              render: (row) => formatToman(row.totalIrr),
            },
          ]}
          rowKey={(row) => `${row.provinceId}:${row.cityId}`}
          rows={snapshot.geographyBreakdown}
        />
      )}
    </>
  );
  return (
    <AccordionPanel
      items={[
        {
          content: detail,
          key: 'impact-detail',
          summary: excluded === 0 ? undefined : `${formatCount(excluded)} کنارگذاشته`,
          title: 'جزئیات گزارش اثر',
        },
      ]}
      label="جزئیات گزارش اثر"
    />
  );
}
